import { API_BASE_URL } from '@synergy/config';
import { Box, Button, Divider, Grid, Theme, Typography } from '@mui/material';
import { DocumentType, InquiryOfferState, Locale, Media, UploadType } from '@synergy/models';
import { Field, FieldProps, Formik } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedDate, FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { Navigate } from 'react-router';
import { PaloDetailView, PaloSideRail, PaloStatusPill } from '@synergy/components-palo';
import {
    PoolButton,
    PoolDropzone,
    PoolFileList,
    PoolModal,
    PoolTextArea,
    PoolTextField,
    PoolTypography,
    ScrollToFieldError,
    WithRouterProps,
    withRouter,
} from '@synergy/components';
import { PrintOutlined } from '@mui/icons-material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { getInquiryOfferStateTranslationId } from '@synergy/utils';
import { removeUploadedFile, resetUploads, setUploads, supplierMediaSelector, uploadFile } from '@synergy/data-access';
import dayjs from 'dayjs';
import printJS from 'print-js';
import urljoin from 'url-join';

import { Account } from '../../common/models/account';
import { AppState } from '../../store/models/app-state';
import { FormValues } from './models/form-values';
import { Inquiry } from '../../common/models/inquiry';
import { Offer } from '../../common/models/offer';
import { OfferAccountPurchase } from '../../common/models/offer-account-purchase';
import { OffersDeclineModal } from '../../common/components/offer-decline/offers-decline-modal';
import { OffersFooter } from './offers-footer';
import { OffersInformationTop } from './offers-information-top';
import { OffersTable } from './components/offers-table';
import { PaloOfferDetailDock } from './offer-detail-sidebar/palo-offer-detail-dock';
import { clearOffers, getOffers, updateOffers } from './actions/offer-actions';
import { declineInquiry } from '../inquiry/actions/inquiry-actions';
import { validationSchema } from './offer-validation-schema';

const styles = (theme: Theme) =>
    createStyles({
        error: {
            textAlign: 'center',
            color: theme.palette.error.main,
            fontWeight: 'bolder',
            border: `1px solid ${theme.palette.error.main}`,
            borderRadius: 4,
            padding: theme.spacing(2),
            margin: theme.spacing(1),
        },
        wrapper: {
            display: 'flex',
            height: '100%',
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
        scrollContainer: {
            flex: 1,
        },
        contentContainer: {
            margin: '0 57px 40px 62px',
        },
        submitContainer: {
            height: 88,
            padding: '0 62px',
            backgroundColor: '#fff',
        },
        notesContainer: {
            marginBottom: 40,
        },
        noteTitle: {
            marginBottom: 17,
        },
        noteTitleDetails: {
            fontSize: 15,
            lineHeight: 1.47,
            marginBottom: theme.spacing(2),
        },
    });

interface OffersReduxProps extends WithStyles<typeof styles>, WrappedComponentProps {
    offerAccountPurchases: OfferAccountPurchase[];
    currency: string;
    locale: Locale;
    inquiry?: Inquiry;
    currentAccount: Account;
    currentAccountBonus: string[];
    notFound: boolean;
    media: Media[];
    mediaBaseUrl: string;
    removeUploadedFile: (docType: DocumentType, docId: number, media: Media) => void;
    uploadFile: (documentType: DocumentType, id: number, file: File, type: UploadType) => void;
    getOffers: (inquiryId: number, supplierId: number) => void;
    clearOffers: () => void;
    updateOffers: (inquiryId: number, offers: Offer[]) => Promise<void>;
    setUploads: (media: Media[]) => void;
    resetUploads: () => void;
    declineInquiry: (inquiryId: number, message: string) => Promise<void>;
}

type OffersProps = OffersReduxProps & WithRouterProps;

interface OffersState {
    showDeclineWarning: boolean;
    showSubmitWarning: boolean;
    showSubmitSuccess: boolean;
    offersReadonly: boolean;
    values?: FormValues;
    selectedOfferItems: { [index: number]: number[] };
}

class OffersBase extends FormComponent<FormValues, OffersProps, OffersState> {
    public override state: OffersState = {
        showDeclineWarning: false,
        showSubmitWarning: false,
        showSubmitSuccess: false,
        offersReadonly: false,
        selectedOfferItems: {},
    };

    private handlePrint = (): void => {
        const url = urljoin(API_BASE_URL, `inquiries/${this.props.inquiry?.id}/pdf-offers`);

        printJS({
            printable: url,
            // Not every browser can print for us; then just show the file.
            onError: () => window.open(url),
        });
    };

    private handleDecline = () => {
        this.setState({ showDeclineWarning: true });
    };

    private handleDeclineCancel = () => {
        this.setState({ showDeclineWarning: false });
    };

    private handleSubmit = (formValues: FormValues) => {
        this.setState({ showSubmitWarning: true, values: formValues });
    };

    private handleDeclineConfirmed = (comment: string) => {
        if (this.props.inquiry) {
            this.props.declineInquiry(this.props.inquiry.id, comment);
            this.props.navigate('/inquiries');
        }
    };

    private handleSubmitConfirmed = async () => {
        if (this.state.values && this.props.inquiry) {
            const offers = this.getOffersFromValues(this.state.values);
            this.setState({ showSubmitWarning: false, values: undefined });
            await this.props.updateOffers(this.props.inquiry.id, offers);
            this.setState({ showSubmitSuccess: true });
        }
    };

    private getOffersFromValues = (values: FormValues): Offer[] => {
        const offers = values.items.map(offerAccountPurchase => offerAccountPurchase.offer);
        offers.forEach(offer => {
            offer.offerItems.forEach(offerItem => {
                if (offerItem.id && this.state.selectedOfferItems[offer.id].includes(offerItem.id)) {
                    const isAlternative = !!offerItem.alternativeNote;
                    offerItem.isOffered = !isAlternative;
                    offerItem.isAlternative = isAlternative;
                } else {
                    offerItem.isOffered = false;
                    offerItem.isAlternative = false;
                }
            });
            offer.termsOfDeliveryContent = values.termsOfDelivery;
            offer.termsOfPaymentContent = values.termsOfPayment;
            offer.supplierBonus = values.supplierBonus;
        });
        return offers;
    };

    private handleSubmitSuccessClosed = () => {
        this.props.navigate('/dashboard');
    };

    private handleSave = async (formValues: FormValues) => {
        if (this.props.inquiry) {
            const offers = this.getOffersFromValues(formValues);
            await this.props.updateOffers(this.props.inquiry.id, offers);
        }
    };

    private handleSelectionChange = (selectedOfferItems: number[], offerId: number) => {
        if (!offerId) {
            return;
        }

        this.state.selectedOfferItems[offerId] = selectedOfferItems;
        const newOfferItems = { ...this.state.selectedOfferItems };
        newOfferItems[offerId] = selectedOfferItems || [];
        this.setState({ selectedOfferItems: newOfferItems });
    };

    public override componentDidMount() {
        this.props.resetUploads();

        const params = this.props.params;
        const id = params['id'] && Number.parseInt(params['id'], 10);

        if (id) {
            this.props.clearOffers();
            this.props.getOffers(id, this.props.currentAccount.id);
        }
    }

    public override UNSAFE_componentWillReceiveProps(nextProps: OffersProps) {
        if (this.props.offerAccountPurchases !== nextProps.offerAccountPurchases) {
            const flatMedia: Media[] = [];
            const offers = nextProps.offerAccountPurchases.map(offerAccountPurchase => offerAccountPurchase.offer);
            offers.forEach(offer => {
                if (offer.media) {
                    offer.media.forEach(media => {
                        media.documentId = offer.id;
                        flatMedia.push(media);
                    });
                }
            });
            this.props.setUploads(flatMedia);

            const inquiryState = nextProps.offerAccountPurchases[0]?.offer.inquiryOfferState;
            if (inquiryState) {
                this.setState({
                    offersReadonly:
                        inquiryState !== InquiryOfferState.Open || dayjs().isAfter(this.props.inquiry?.endDate, 'day'),
                });
            }
        }
    }

    // Correctly presets terms on all offers
    private getInitialValues = (): FormValues => {
        if (this.props.offerAccountPurchases.length === 0) {
            return { items: [], supplierBonus: '', termsOfDelivery: '', termsOfPayment: '' };
        }

        const accountTermsOfDelivery = this.props.currentAccount.termsOfDelivery || '';
        const accountTermsOfPayment = this.props.currentAccount.termsOfPayment || '';
        const accountBonus =
            this.props.currentAccountBonus.length > 0 ? this.props.currentAccountBonus.join('\r\n') : '';

        const termsOfDelivery =
            this.props.offerAccountPurchases[0].offer.termsOfDeliveryContent || accountTermsOfDelivery;
        const termsOfPayment = this.props.offerAccountPurchases[0].offer.termsOfPaymentContent || accountTermsOfPayment;
        const supplierBonus =
            this.props.offerAccountPurchases[0].offer.supplierBonus ||
            accountBonus ||
            this.props.intl.formatMessage({ id: 'app.default.poolBonus' });
        const items = [...this.props.offerAccountPurchases];
        items.forEach(accountPurchase =>
            accountPurchase.offer.offerItems.forEach(
                offerItem => (offerItem.isSelected = offerItem.isOffered || offerItem.isAlternative)
            )
        );

        return {
            items,
            supplierBonus,
            termsOfDelivery,
            termsOfPayment,
        };
    };

    private handleFileDrop = (acceptedFiles: File[]) => {
        const id = this.props.offerAccountPurchases[0]?.offer.id;
        if (!id) {
            return;
        }

        const deduplicated = acceptedFiles.filter(
            file =>
                !this.props.media.some(
                    media => media.originalName === file.name && media.size === file.size && media.documentId === id
                )
        );

        if (deduplicated.length > 0) {
            deduplicated.forEach(file => {
                this.props.uploadFile(DocumentType.Offer, id, file, UploadType.Supplier);
            });
        }
    };

    private handleFileRemove = (media: Media) => {
        if (this.props.inquiry?.id) {
            this.props.removeUploadedFile(DocumentType.Offer, this.props.inquiry?.id, media);
        }
    };

    private handleUploadCancel = (media: Media) => {
        if (media.cancel) {
            media.cancel();
        }
    };

    public override render() {
        const {
            classes,
            currency,
            locale,
            inquiry,
            notFound,
            currentAccount,
            currentAccountBonus,
            media,
            mediaBaseUrl,
        } = this.props;

        if (notFound) {
            return <Navigate to="/not-found" />;
        }

        const submitActionChildren = [
            <PoolButton key="2" styleType="firstLevel" onClick={() => this.setState({ showSubmitWarning: false })}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="1" color="secondary" styleType="firstLevel" onClick={this.handleSubmitConfirmed}>
                <FormattedMessage id="app.offers.submit" />
            </PoolButton>,
        ];

        const submitSuccessActionChildren = [
            <PoolButton key="2" variant="contained" color="primary" onClick={this.handleSubmitSuccessClosed}>
                <FormattedMessage id="app.common.ok" />
            </PoolButton>,
        ];

        const initialValues: FormValues = this.getInitialValues();
        const termsOfDeliveryReadonly = !!currentAccount.termsOfDelivery;
        const termsOfPaymentReadonly = !!currentAccount.termsOfPayment;
        const bonusReadonly = currentAccountBonus.length > 0;

        const checkSelected = () => {
            const keys = Object.keys(this.state.selectedOfferItems).map(key => +key);
            return keys.every(key => {
                const items = this.state.selectedOfferItems[key];
                return items.length === 0;
            });
        };

        const inquiryState = this.props.offerAccountPurchases[0]?.offer.inquiryOfferState;
        const offerState = inquiry?.accountPurchases?.[0]?.offers?.[0]?.inquiryOfferState;

        return (
            <>
                <Formik
                    onSubmit={this.handleSubmit}
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                    enableReinitialize={true}
                >
                    {formikProps => (
                        <>
                            <ScrollToFieldError />
                            <PaloDetailView
                                onBack={() => this.props.navigate(-1)}
                                number={inquiry?.id}
                                title={
                                    <FormattedMessage
                                        id={inquiry?.isAuction ? 'app.common.auction' : 'app.document.inquiry'}
                                    />
                                }
                                status={
                                    offerState && (
                                        <PaloStatusPill
                                            status={offerState}
                                            label={
                                                <FormattedMessage id={getInquiryOfferStateTranslationId(offerState)} />
                                            }
                                        />
                                    )
                                }
                                meta={
                                    inquiry
                                        ? [
                                              {
                                                  label: <FormattedMessage id="app.table.column.endDate" />,
                                                  value: (
                                                      <FormattedDate
                                                          value={inquiry.endDate}
                                                          year="numeric"
                                                          month="short"
                                                          day="2-digit"
                                                      />
                                                  ),
                                              },
                                          ]
                                        : undefined
                                }
                                actions={
                                    <>
                                        <Button
                                            variant="text"
                                            color="primary"
                                            startIcon={<PrintOutlined />}
                                            disabled={!inquiry?.id}
                                            onClick={this.handlePrint}
                                        >
                                            <FormattedMessage id="app.common.print" />
                                        </Button>
                                        <Box sx={{ flex: 1 }} />
                                        <Button
                                            variant="text"
                                            color="primary"
                                            disabled={
                                                !this.state.offersReadonly ||
                                                inquiryState === InquiryOfferState.Closed ||
                                                dayjs().isAfter(this.props.inquiry?.endDate, 'day')
                                            }
                                            onClick={() => this.setState({ offersReadonly: false })}
                                        >
                                            <FormattedMessage id="app.offers.reactivate" />
                                        </Button>
                                        <Button
                                            id="declineInquiry"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={this.handleDecline}
                                            disabled={this.state.offersReadonly}
                                        >
                                            <FormattedMessage
                                                id={
                                                    inquiry?.isAuction
                                                        ? 'app.offers.declineAuction'
                                                        : 'app.offers.decline'
                                                }
                                            />
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={formikProps.submitForm}
                                            disabled={this.state.offersReadonly || checkSelected()}
                                        >
                                            <FormattedMessage id="app.offers.submit" />
                                        </Button>
                                    </>
                                }
                                dock={
                                    inquiry ? (
                                        <PaloSideRail
                                            sections={[
                                                {
                                                    key: 'details',
                                                    label: <FormattedMessage id="app.common.details" />,
                                                    content: (
                                                        <PaloOfferDetailDock
                                                            inquiry={inquiry}
                                                            offers={formikProps.values.items}
                                                        />
                                                    ),
                                                },
                                            ]}
                                            collapsedLabel={<FormattedMessage id="app.common.details" />}
                                        />
                                    ) : undefined
                                }
                            >
                                <Box id="scroll-container" sx={{ height: '100%', overflowY: 'auto' }}>
                                    {formikProps.values.items.map((item, index) => (
                                        <section
                                            id={`customer-${item.accountPurchase.id}`}
                                            key={item.accountPurchase.id}
                                        >
                                            <div className={classes.contentContainer}>
                                                {inquiry && (
                                                    <OffersInformationTop
                                                        inquiry={inquiry}
                                                        accountPurchase={item.accountPurchase}
                                                        index={index}
                                                    />
                                                )}
                                                <Grid container={true} xs={12}>
                                                    <Grid item={true} xs={4}>
                                                        {/* One line: an offer number is a number, and the
                                                            multi-line box invited a paragraph (#2214, R-48). */}
                                                        <Field name={`items.${index}.offer.supplierOfferNumber`}>
                                                            {({ field }: FieldProps<Offer>) => (
                                                                <PoolTextField
                                                                    isFullWidth={true}
                                                                    label={
                                                                        <FormattedMessage id="app.label.supplierOfferNumber" />
                                                                    }
                                                                    readOnly={this.state.offersReadonly}
                                                                    {...field}
                                                                />
                                                            )}
                                                        </Field>
                                                    </Grid>
                                                </Grid>
                                                <OffersTable
                                                    itemIndex={index}
                                                    offer={item.offer}
                                                    currency={currency}
                                                    locale={locale}
                                                    readonly={this.state.offersReadonly}
                                                    onSelectionChange={selectedItems =>
                                                        this.handleSelectionChange(selectedItems, item.offer.id)
                                                    }
                                                />

                                                <PoolTypography
                                                    typography="h6"
                                                    id="app.common.note"
                                                    className={classes.noteTitle}
                                                />
                                                <PoolTypography
                                                    typography="body1"
                                                    id="app.common.noteDetails"
                                                    className={classes.noteTitleDetails}
                                                />
                                                {/* Both columns end on the same line: the upload area
                                                    grows with the remark beside it instead of standing
                                                    on a fixed height of its own (#2214, R-47). */}
                                                <Grid container={true} spacing={3} alignItems="stretch">
                                                    {/* The remark takes the height of the column, so it ends
                                                        on the same line as the upload area beside it instead
                                                        of stopping at its own five rows (#2214, R-47). */}
                                                    <Grid
                                                        item={true}
                                                        lg={7}
                                                        xs={12}
                                                        sx={{
                                                            display: 'flex',
                                                            '& > div': { width: '100%' },
                                                            '& .MuiFormControl-root': { height: '100%' },
                                                            '& .MuiInputBase-root': {
                                                                height: '100%',
                                                                alignItems: 'flex-start',
                                                            },
                                                        }}
                                                    >
                                                        <Field name={`items.${index}.offer.noteFromSupplier`}>
                                                            {({ field }: FieldProps<Offer>) => (
                                                                // No label of its own: "Bemerkung" already stands
                                                                // as the heading right above, and a second one
                                                                // pushed the box 17px below the upload area
                                                                // beside it (#2214, R-47).
                                                                <PoolTextArea
                                                                    rows="5"
                                                                    rowsMax="50"
                                                                    readOnly={this.state.offersReadonly}
                                                                    {...field}
                                                                />
                                                            )}
                                                        </Field>
                                                    </Grid>
                                                    {/* A plain flex column rather than a nested Grid: Grid's
                                                        spacing works with negative margins, which made this
                                                        column taller than the row it sits in and left the
                                                        upload area on its own minimum height (#2214, R-47). */}
                                                    <Grid item={true} lg={5} xs={12}>
                                                        <Box
                                                            sx={{
                                                                height: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '24px',
                                                            }}
                                                        >
                                                            <Box sx={{ flex: 1, display: 'flex' }}>
                                                                <PoolDropzone
                                                                    onDrop={this.handleFileDrop}
                                                                    disabled={this.state.offersReadonly}
                                                                />
                                                            </Box>
                                                            <PoolFileList
                                                                acceptedFiles={media}
                                                                onDeleteClicked={this.handleFileRemove}
                                                                onCancelClicked={this.handleUploadCancel}
                                                                downloadBaseUrl={mediaBaseUrl}
                                                                readOnly={this.state.offersReadonly}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </div>
                                            <Divider />
                                        </section>
                                    ))}
                                    <Field name="items">
                                        {({ field, form }: FieldProps<FormValues>) =>
                                            this.hasError(form, field.name) && (
                                                <Typography className={classes.error}>
                                                    {this.getError(form, field.name)}
                                                </Typography>
                                            )
                                        }
                                    </Field>
                                    <OffersFooter
                                        onSubmit={formikProps.submitForm}
                                        onDecline={this.handleDecline}
                                        readonly={this.state.offersReadonly}
                                        bonusReadonly={bonusReadonly}
                                        termsOfDeliveryReadonly={termsOfDeliveryReadonly}
                                        termsOfPaymentReadonly={termsOfPaymentReadonly}
                                        inquiry={inquiry}
                                    />
                                </Box>
                            </PaloDetailView>
                        </>
                    )}
                </Formik>
                <OffersDeclineModal
                    open={this.state.showDeclineWarning}
                    cancel={this.handleDeclineCancel}
                    decline={this.handleDeclineConfirmed}
                    inquiry={inquiry}
                />
                <PoolModal
                    actionChildren={submitActionChildren}
                    handleClose={() => this.setState({ showSubmitWarning: false })}
                    isVisible={this.state.showSubmitWarning}
                    title={<FormattedMessage id="app.header.warning" />}
                >
                    <PoolTypography id="app.offers.submitWarningContent" style={{ textAlign: 'center' }} />
                </PoolModal>
                <PoolModal
                    actionChildren={submitSuccessActionChildren}
                    handleClose={this.handleSubmitSuccessClosed}
                    isVisible={this.state.showSubmitSuccess}
                    title={<FormattedMessage id="app.header.notice" />}
                >
                    <PoolTypography id="app.offers.submitSuccessContent" />
                </PoolModal>
            </>
        );
    }
}

const offersWithRouter = withRouter(OffersBase);

const mapStateToProps = (state: AppState) => ({
    offerAccountPurchases: state.offers.offerAccountPurchase,
    inquiry: state.offers.inquiry,
    currency: state.settings.currency,
    locale: state.locale,
    currentAccount: state.currentAccount,
    notFound: state.offers.notFound,
    currentAccountBonus: state.currentAccountBonus,
    media: supplierMediaSelector(state),
    mediaBaseUrl: state.settings.mediaBaseUrl,
});

const ConnectedOffers = connect(mapStateToProps, {
    getOffers,
    clearOffers,
    updateOffers,
    resetUploads,
    setUploads,
    declineInquiry,
    uploadFile,
    removeUploadedFile,
})(offersWithRouter);

export const Offers = withStyles(styles)(injectIntl(ConnectedOffers));
